import React from 'react';

export default function AnalysisSummary({ hitFrequencies, cardSelections, bets, denomination, selectedSpotSize, onSelectSpotSize, paytable, activePaytable }) {
  const formatCurrency = (value) => {
    return `$${value.toFixed(2)}`;
  };

  // Calculate summary by spot count
  const getSummaryBySpots = () => {
    const summary = {};
    
    Object.keys(bets).forEach(card => {
      if (bets[card] > 0) {
        const spots = cardSelections[card].length;
        const freq = hitFrequencies[card] || {};
        const totalGames = Object.keys(freq)
          .filter(key => key !== 'totalWon')
          .reduce((sum, key) => sum + (freq[key] || 0), 0);
        const totalWon = freq.totalWon || 0;
        const totalWagered = totalGames * bets[card] * denomination;
        
        if (!summary[spots]) {
          summary[spots] = { totalWon: 0, totalWagered: 0, cards: 0 };
        }
        
        summary[spots].totalWon += totalWon;
        summary[spots].totalWagered += totalWagered;
        summary[spots].cards += 1;
      }
    });
    
    return summary;
  };

  const summary = getSummaryBySpots();
  const spotCounts = Object.keys(summary).sort((a, b) => a - b);

  // Calculate totals across all spot counts
  const totals = spotCounts.reduce((acc, spots) => {
    acc.cards += summary[spots].cards;
    acc.totalWagered += summary[spots].totalWagered;
    acc.totalWon += summary[spots].totalWon;
    return acc;
  }, { cards: 0, totalWagered: 0, totalWon: 0 });

  // Binomial coefficient C(n, k)
  const combination = (n, k) => {
    if (k > n) return 0;
    if (k === 0 || k === n) return 1;
    let result = 1;
    for (let i = 0; i < k; i++) {
      result *= (n - i);
      result /= (i + 1);
    }
    return result;
  };

  // Calculate theoretical EV for a given spot size
  const calculateEV = (spots) => {
    const n = parseInt(spots);
    const paytableObj = paytable?.[activePaytable] || {};
    const payouts = paytableObj[spots] || {};
    const totalCombinations = combination(80, 20);
    let expectedValue = 0;
    for (let hits = 0; hits <= n; hits++) {
      const probability = (combination(n, hits) * combination(80 - n, 20 - hits)) / totalCombinations;
      const payout = payouts[hits] || 0;
      expectedValue += probability * payout;
    }
    return expectedValue;
  };

  // Get paytable and activePaytable from window (App passes them)
  if (spotCounts.length === 0) {
    return (
      <aside className="analysis-summary">
        <div className="as-header">Summary by Spots</div>
        <div style={{ color: '#aaa', fontStyle: 'italic', fontSize: '10px', padding: '4px 2px' }}>No data yet.</div>
      </aside>
    );
  }

  return (
    <aside className="analysis-summary">
      <div className="as-header">Summary by Spots</div>
      <div className="as-grid">
        <div className="as-row as-row-header">
          <div
            className="as-cell as-cell-label"
            style={selectedSpotSize ? { background: '#ffd600', color: '#222' } : {}}
            title="Click a spot count to filter the Card Analysis table by that spot size. Click again to remove the filter."
          >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
              <span style={{ fontWeight: 700 }}>Spots</span>
              {selectedSpotSize ? (
                <span
                  onClick={() => onSelectSpotSize && onSelectSpotSize(selectedSpotSize)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    background: '#333', color: '#ffd600', fontWeight: 700,
                    fontSize: '9px', borderRadius: '8px', padding: '1px 5px',
                    cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid #ffd600',
                  }}
                  title="Click to clear filter"
                >
                  {selectedSpotSize}-spot <span style={{ fontSize: '10px', lineHeight: 1 }}>✕</span>
                </span>
              ) : (
                <span style={{ color: '#888', fontSize: '8px', fontWeight: 400, letterSpacing: '0.02em' }}>
                  ▾ click to filter
                </span>
              )}
            </div>
          </div>
          {spotCounts.map(spots => (
            <div
              key={spots}
              className={`as-cell as-cell-header${selectedSpotSize === spots ? ' as-cell-selected' : ''}`}
              style={{ cursor: 'pointer', background: selectedSpotSize === spots ? '#ffd600' : undefined, fontWeight: selectedSpotSize === spots ? 900 : undefined }}
              onClick={() => onSelectSpotSize && onSelectSpotSize(spots)}
            >
              {spots}
            </div>
          ))}
          <div className="as-cell as-cell-header">Total</div>
        </div>
        <div className="as-row">
          <div className="as-cell as-cell-label">Cards</div>
          {spotCounts.map(spots => (
            <div key={spots} className="as-cell">{summary[spots].cards}</div>
          ))}
          <div className="as-cell">{totals.cards}</div>
        </div>
        <div className="as-row">
          <div className="as-cell as-cell-label">Wagered</div>
          {spotCounts.map(spots => (
            <div key={spots} className="as-cell">{formatCurrency(summary[spots].totalWagered)}</div>
          ))}
          <div className="as-cell">{formatCurrency(totals.totalWagered)}</div>
        </div>
        <div className="as-row">
          <div className="as-cell as-cell-label">Won</div>
          {spotCounts.map(spots => (
            <div key={spots} className="as-cell">{formatCurrency(summary[spots].totalWon)}</div>
          ))}
          <div className="as-cell">{formatCurrency(totals.totalWon)}</div>
        </div>
        <div className="as-row">
          <div className="as-cell as-cell-label">Return</div>
          {spotCounts.map(spots => {
            const returnPct = summary[spots].totalWagered > 0 
              ? (summary[spots].totalWon / summary[spots].totalWagered * 100) 
              : 0;
            const colorClass = returnPct >= 100 ? 'return-good' : returnPct >= 80 ? 'return-fair' : 'return-poor';
            return (
              <div key={spots} className={`as-cell ${colorClass}`}>
                {returnPct.toFixed(2)}%
              </div>
            );
          })}
          <div className={`as-cell ${
            totals.totalWagered > 0 
              ? (totals.totalWon / totals.totalWagered * 100) >= 100 
                ? 'return-good' 
                : (totals.totalWon / totals.totalWagered * 100) >= 80 
                  ? 'return-fair' 
                  : 'return-poor'
              : ''
          }`}>
            {totals.totalWagered > 0 ? ((totals.totalWon / totals.totalWagered * 100).toFixed(2) + '%') : '0.00%'}
          </div>
        </div>
        {/* Theoretical EV row */}
        <div className="as-row">
          <div className="as-cell as-cell-label">Theoretical EV</div>
          {spotCounts.map(spots => {
            const ev = calculateEV(spots);
            return (
              <div key={spots} className="as-cell">{(ev * 100).toFixed(2)}%</div>
            );
          })}
          <div className="as-cell">
            {(() => {
              // Total theoretical EV weighted by wagered
              const totalWagered = totals.totalWagered;
              if (totalWagered === 0) return '';
              let weightedEV = 0;
              spotCounts.forEach(spots => {
                weightedEV += calculateEV(spots) * summary[spots].totalWagered;
              });
              return (weightedEV / totalWagered * 100).toFixed(2) + '%';
            })()}
          </div>
        </div>
        {/* Delta row: Actual - Theoretical */}
        <div className="as-row">
          <div className="as-cell as-cell-label">Delta</div>
          {spotCounts.map(spots => {
            const returnPct = summary[spots].totalWagered > 0 
              ? (summary[spots].totalWon / summary[spots].totalWagered * 100) 
              : 0;
            const theoreticalPct = calculateEV(spots) * 100;
            const delta = returnPct - theoreticalPct;
            const deltaClass = delta > 2 ? 'return-good' : delta < -2 ? 'return-poor' : 'return-fair';
            return (
              <div key={spots} className={`as-cell ${deltaClass}`}>{delta.toFixed(2)}%</div>
            );
          })}
          {(() => {
            const totalWagered = totals.totalWagered;
            if (totalWagered === 0) return <div className="as-cell"></div>;
            let weightedEV = 0;
            spotCounts.forEach(spots => {
              weightedEV += calculateEV(spots) * summary[spots].totalWagered;
            });
            const theoreticalTotal = weightedEV / totalWagered * 100;
            const actualTotal = totals.totalWon / totals.totalWagered * 100;
            const totalDelta = actualTotal - theoreticalTotal;
            const deltaClass = totalDelta > 2 ? 'return-good' : totalDelta < -2 ? 'return-poor' : 'return-fair';
            return (
              <div className={`as-cell ${deltaClass}`}>{totalDelta.toFixed(2)}%</div>
            );
          })()}
        </div>

        {/* Total Bet % Row: lowest = green, highest = red, mid = yellow */}
        <div className="as-row">
          <div className="as-cell as-cell-label">Total Bet %</div>
          {(() => {
            const betPcts = spotCounts.map(spots => totals.totalWagered > 0 ? (summary[spots].totalWagered / totals.totalWagered * 100) : 0);
            return spotCounts.map((spots, i) => (
              <div key={spots} className="as-cell">{betPcts[i].toFixed(2)}%</div>
            ));
          })()}
          <div className="as-cell">100.00%</div>
        </div>

        {/* Total Won % Row: highest = green, lowest = red, mid = yellow */}
        <div className="as-row">
          <div className="as-cell as-cell-label">Total Won %</div>
          {(() => {
            const wonPcts = spotCounts.map(spots => totals.totalWon > 0 ? (summary[spots].totalWon / totals.totalWon * 100) : 0);
            return spotCounts.map((spots, i) => (
              <div key={spots} className="as-cell">{wonPcts[i].toFixed(2)}%</div>
            ));
          })()}
          <div className="as-cell">100.00%</div>
        </div>

        {/* Won/Bets Row: Total Won% divided by Total Bet% */}
        <div className="as-row">
          <div className="as-cell as-cell-label" title="For each spot size: its share of total winnings minus its share of total bets. Positive = that spot size punched above its weight (won more than it cost relative to others). Negative = it underperformed its share. The values always sum to zero across all spot sizes.">Won % - Bet %</div>
          {(() => {
            const betPcts = spotCounts.map(spots => totals.totalWagered > 0 ? (summary[spots].totalWagered / totals.totalWagered * 100) : 0);
            const wonPcts = spotCounts.map(spots => totals.totalWon > 0 ? (summary[spots].totalWon / totals.totalWon * 100) : 0);
            const diffs = spotCounts.map((spots, i) => wonPcts[i] - betPcts[i]);
            // Color: >5 good, <-5 poor, else fair
            return diffs.map((diff, i) => {
              let colorClass = '';
              if (diff > 5) colorClass = 'return-good';
              else if (diff < -5) colorClass = 'return-poor';
              else colorClass = 'return-fair';
              return (
                <div key={spotCounts[i]} className={`as-cell ${colorClass}`}>{diff.toFixed(2)}%</div>
              );
            });
          })()}
          <div className="as-cell return-fair" title="Always 0.00% — the gains and losses across spot sizes balance out by definition.">0.00%</div>
        </div>

        {/* Contribution Index Row: (Total Won% - Total Bet%) */}
        <div className="as-row">
          <div className="as-cell as-cell-label" title="Contribution Index (C.I.): Measures how much each spot size contributed to total winnings relative to its share of total bets, plus how much the actual return exceeded theoretical EV. Calculated as (Won % - Bet %) + (Return - Theoretical EV). Positive values mean the spot size contributed more than expected; negative means less.">C.I. ⓘ</div>
          {(() => {
            const betPcts = spotCounts.map(spots => totals.totalWagered > 0 ? (summary[spots].totalWagered / totals.totalWagered * 100) : 0);
            const wonPcts = spotCounts.map(spots => totals.totalWon > 0 ? (summary[spots].totalWon / totals.totalWon * 100) : 0);
            const returnPcts = spotCounts.map(spots => summary[spots].totalWagered > 0 ? (summary[spots].totalWon / summary[spots].totalWagered * 100) : 0);
            const evPcts = spotCounts.map(spots => calculateEV(spots) * 100);
            const perfIndices = spotCounts.map((spots, i) => (wonPcts[i] - betPcts[i]) + (returnPcts[i] - evPcts[i]));
            // Color: >5 good, <-5 poor, else fair
            return perfIndices.map((pi, i) => {
              let colorClass = '';
              if (pi > 5) colorClass = 'return-good';
              else if (pi < -5) colorClass = 'return-poor';
              else colorClass = 'return-fair';
              return (
                <div key={spotCounts[i]} className={`as-cell ${colorClass}`}>{pi.toFixed(2)}%</div>
              );
            });
          })()}
          {(() => {
            if (totals.totalWagered === 0) return <div className="as-cell"></div>;
            const actualReturn = totals.totalWon / totals.totalWagered * 100;
            let weightedEV = 0;
            spotCounts.forEach(spots => {
              weightedEV += calculateEV(spots) * summary[spots].totalWagered;
            });
            const weightedEVPct = weightedEV / totals.totalWagered * 100;
            const sessionCI = actualReturn - weightedEVPct;
            const colorClass = sessionCI > 2 ? 'return-good' : sessionCI < -2 ? 'return-poor' : 'return-fair';
            return (
              <div className={`as-cell ${colorClass}`} title={`Session actual return (${actualReturn.toFixed(2)}%) minus weighted theoretical EV (${weightedEVPct.toFixed(2)}%)`}>
                {sessionCI.toFixed(2)}%
              </div>
            );
          })()}
        </div>
      </div>
    </aside>
  );
}
